<template>
  <div>
    <v-form ref="form" v-model="valid">
      <div class="d-flex">
        <student-lookup
          class="my-4 flex-grow-1"
          v-model="selectedStudent"
          :required="
            selectedStudents && selectedStudents.length > 0 ? false : true
          "
        />
        <v-btn
          @click="addMore"
          v-if="selectedStudent"
          text
          class="my-auto ml-2"
        >
          Add More
        </v-btn>
      </div>
      <div class="mb-4" v-if="selectedStudents && selectedStudents.length > 0">
        <v-chip-group>
          <v-chip
            close
            @click:close="removeSelectedStudent(ss.id)"
            v-for="ss in selectedStudents"
            :key="ss.id"
          >
            {{ ss.fullName }}
          </v-chip>
        </v-chip-group>
      </div>

      <div v-if="hasSelectedStudent" class="d-flex justify-space-between">
        <teacher-lookup
          class="flex-grow-1"
          v-if="!isClassroomAssignment"
          v-model="selectedTeacher"
          :required="false"
          @change="selectedClassroom = null"
          dense
          label="Assigned Teacher"
        />
        <span v-if="notAssigned" class="mx-4 my-auto"> or </span>
        <v-autocomplete
          v-if="!isTeacherAssignment"
          @change="selectedTeacher = null"
          :items="centerClassrooms"
          v-model="selectedClassroom"
          item-value="id"
          item-text="roomNumber"
          label="Assigned Classroom"
          dense
          clearable
        />
      </div>
      <v-autocomplete
        v-if="hasSelectedStudent"
        v-model="selectedProgram"
        :items="programs"
        item-value="id"
        item-text="name"
        label="Select Program"
        :rules="[rules.required]"
        required
        clearable
      />
      <date-picker
        label="Start Date"
        :defaultNowOnNull="false"
        :show-hint="false"
        :show-icon="false"
        v-model="selectedDate"
        :outlined="false"
        clearable
      />
      <v-text-field
        v-model="internalStartTime"
        label="Start Time*"
        type="time"
        :rules="[rules.required, validTimeRange]"
        required
      />
      <v-text-field
        v-model="internalEndTime"
        label="End Time*"
        type="time"
        :rules="[rules.required, validTimeRange]"
        required
      />
      <v-textarea v-model="notes" label="Notes" :rows="1" auto-grow />
      <v-checkbox v-model="isRecurring" label="Recurring Schedule?" />
      <div v-if="isRecurring">
        <date-picker
          label="End Date (Optional)"
          :defaultNowOnNull="false"
          :show-hint="false"
          :show-icon="false"
          v-model="endDate"
          :outlined="false"
          clearable
        />
        <div class="d-flex justify-space-around">
          <v-checkbox
            v-for="(day, index) in daysOfWeek"
            :key="index"
            v-model="day.selected"
            :label="day.label"
          />
        </div>
      </div>
      <v-card-actions>
        <v-btn
          class="mx-auto"
          color="green darken-1"
          text
          :loading="isLoading"
          @click="createSchedule"
        >
          Create Schedule
        </v-btn>
        <v-btn color="blue darken-1" text @click="resetSelections(); onClose()"> Close </v-btn>
      </v-card-actions>
    </v-form>
  </div>
</template>

<script>
import moment from 'moment'
import dialogMixin from './dialogMixin.js'
import Axios from "axios";
import { mapActions } from "vuex";
const StudentLookup = () => import("@/components/student/StudentLookup.vue");
const TeacherLookup = () => import("@/components/teacher/TeacherLookup.vue");
import { EventBus } from "@/EventBus.js";
import { openOverlapScheduleErrors } from '@/components/schedule/eventHandler.js'

export default {
  mixins: [dialogMixin],
  props: {
    defaultStudent: {
      type: Object,
      required: false,
    },
    selectedTeacher: {
      type: Object,
      required: false,
    },
    selectedClassroom: {
      type: Number,
      required: false,
    },
    selectedDate: {
      type: Date,
      required: false,
    },
    recurringEndDate: {
      type: Date,
      required: false,
    },
    startTime: {
      type: String,
      default: null,
    },
    endTime: {
      type: String,
      default: null,
    },
    recurring: {
      type: Boolean,
      default: false,
    },
    recurringDays: {
      type: Array,
      required: false,
    }
  },
  components: { StudentLookup, TeacherLookup },
  data: () => ({
    selectedStudent: null,
    selectedStudents: [],
    selectedProgram: null,
    isRecurring: false,
    endDate: null,
    internalStartTime: null,
    internalEndTime: null,
    notes: null,
    programs: [],
    isLoading: false,
    rules: {
      required: (value) => !!value || "Required.",
    },
    daysOfWeek: [
      { label: "Sun", selected: false },
      { label: "Mon", selected: false },
      { label: "Tue", selected: false },
      { label: "Wed", selected: false },
      { label: "Thu", selected: false },
      { label: "Fri", selected: false },
      { label: "Sat", selected: false },
    ],
  }),
  computed: {
    centerClassrooms() {
      if (this.selectedStudent && this.selectedStudent.centerId) {
        return this.classrooms.filter(
          (c) => c.centerId == this.selectedStudent.centerId
        );
      } else {
        return this.classrooms;
      }
    },
    isClassroomAssignment() {
      return this.selectedClassroom ? true : false;
    },
    isTeacherAssignment() {
      return this.selectedTeacher ? true : false;
    },
    notAssigned() {
      return !this.selectedTeacher && !this.selectedClassroom;
    },
    hasSelectedStudent() {
      return (
        (this.selectedStudent && this.selectedStudent.id) ||
        (this.selectedStudents && this.selectedStudents.length > 0)
      );
    },
  },
  watch: {
    selectedTeacher(newVal) {
      this.selectedTeacher = newVal;
    },
    startTime(newVal) {
      this.internalStartTime = newVal;
    },
    endTime(newVal) {
      this.internalEndTime = newVal;
    },
    defaultStudent(newVal){
      this.selectedStudent = null;
      if(newVal){
        this.selectedStudent = {...this.defaultStudent};
      }
    },
    selectedStudent(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.loadPrograms(newVal.centerId);
      }
    },
  },
  mounted() {
    this.internalStartTime = this.startTime;
    this.internalEndTime = this.endTime;
    if (this.recurring) {
      this.isRecurring = this.recurring;
      this.endDate = this.recurringEndDate;
      this.daysOfWeek.forEach((dow) => {
        if (this.recurringDays.includes(dow.label)) {
          dow.selected = true;
        }
      });
    }

    if(this.defaultStudent){
      this.selectedStudent = {...this.defaultStudent};
    }
  },
  methods: {
    ...mapActions(["addErrorMessage", "addSuccessMessage", "clearMessage"]),
    validTimeRange() {
      if (this.internalStartTime && this.internalEndTime) {
        return (
          this.internalEndTime > this.internalStartTime ||
          "End time cannot be earlier than start time."
        );
      }
      return true;
    },
    validateDaysOfWeek() {
      if (this.isRecurring) {
        const atLeastOneDaySelected = this.daysOfWeek.some(
          (day) => day.selected
        );
        if (!atLeastOneDaySelected) {
          return "At least one day of the week must be selected for recurring schedules.";
        }
      }
      return true;
    },
    resetSelections() {
      this.selectedStudent = null;
      this.selectedStudents = [];
      this.selectedProgram = null;
      this.isRecurring = false;
      this.selectedTeacher = null;
      this.selectedClassroom = null;
    },
    loadPrograms(center) {
      const params = { centerId: center };
      this.$axios.get("/program/get-by-center", { params }).then((resp) => {
        this.programs = resp.data;
      });
    },
    addMore() {
      const student = JSON.parse(JSON.stringify(this.selectedStudent));

      const isDuplicate = this.selectedStudents.some(
        (existingStudent) => existingStudent.id === student.id
      );

      if (!isDuplicate) {
        this.selectedStudents.push(student);
        this.selectedStudent = null;
      } else {
        console.log(`Student with id ${student.id} is already selected.`);
      }
    },
    removeSelectedStudent(studentId) {
      this.selectedStudents = this.selectedStudents.filter(
        (student) => student.id !== studentId
      );
    },
    createSchedule() {
      if (!this.$refs.form.validate()) {
        return;
      }

      // Custom validation for days of the week
      const daysOfWeekValid = this.validateDaysOfWeek();
      if (daysOfWeekValid !== true) {
        this.addErrorMessage(daysOfWeekValid);
        return;
      }

      this.isLoading = true;

      let studentsToSchedule =
        this.selectedStudents.length > 0
          ? this.selectedStudents
          : [this.selectedStudent];

      let requests = studentsToSchedule.map((student) => {
        const data = {
          centerId: student.centerId,
          teacherId: this.selectedTeacher ? this.selectedTeacher.id : null,
          classroomId: this.selectedClassroom,
          studentId: student.id,
          programId: this.selectedProgram,
          startTime: this.internalStartTime,
          endTime: this.internalEndTime,
          recurring: this.isRecurring,
          startDate: moment(this.selectedDate).format('YYYY-MM-DD'),
          endDate: this.endDate ? moment(this.endDate).format('YYYY-MM-DD'):null,
          notes: this.notes,
          daysOfWeek: this.daysOfWeek
            .filter((dow) => dow.selected)
            .map((dow) => dow.label),
          timezone: new Date().getTimezoneOffset(),
        };

        return this.$axios
          .post("/student-schedule", data)
          .catch((error) => {
            // Handle individual error here and access corresponding request parameters
            console.error(`Error for student with id ${student.id}:`, error);
            this.addErrorMessage(
              `Error creating schedule for student ${student.firstName}` || error.data.message
            );

            if(error.data && error.data.code == "OVERLAPPING_SCHEDULES"){
              const overlapSchedules = error.data.args[0];
              // Open a new dialog with overlap details
              openOverlapScheduleErrors( {schedule: data, student, overlappingSchedules: overlapSchedules} );
            }
            return Promise.reject(error); // propagate the error
          });
      });

      Axios.all(requests)
        .then(
          Axios.spread((...responses) => {
            this.isLoading = false;

            let savedSchedules = responses.map((resp) => resp.data.data);

            this.addSuccessMessage("Student schedule added");

            this.resetSelections();
            this.addSuccessMessage("Schedule added")
            this.$emit('on-close')
            EventBus.$emit('reload-chart', { savedSchedules })
          })
        )
        .catch((error) => {
          this.isLoading = false;
          this.$emit("on-error", error);
          
        });
    },
  },
};
</script>
